import { h, Component } from "preact";
import style from "./StandardSummary.sass";
import i18next from "../../i18n/i18n";
import LoaderSpinner from "../Loaders/LoaderSpinner";
import PCOLSummaryBox from "../PCOLSummaryBox/PCOLSummaryBox";
import { hotjarDispatch, validateRetry } from "../../utils/helpers";
import RetryTime from "../RetryTime/RetryTime";
import PropTypes from "prop-types";
import statusApprovedIcon from "../../assets/icons/status-approved.svg";
import statusErrorIcon from "../../assets/icons/status-error.svg";
import { downloadReceiptPdf } from "../../utils/receiptPdf";
import statusPendingIcon from "../../assets/icons/status-pending.svg";
import retryIcon from "../../assets/icons/retry.svg";
import logoPSE from "../../assets/original-icons/pse-logo.png";
import logoVisa from "../../assets/original-icons/visa.svg";
import logoMastercard from "../../assets/original-icons/master-card.svg";
import logoAmex from "../../assets/original-icons/american-express.svg";
import logoDiners from "../../assets/original-icons/diners-club.svg";
import logoBancolombiaTransfer from "../../assets/original-icons/bc.svg";
import logoBnpl from "../../assets/original-icons/bnpl-logo.svg";
import logoDaviplata from "../../assets/original-icons/daviplata-logo.svg";
import logoSuPlusPay from "../../assets/original-icons/su-plus-pay-logo.svg";
import logoPCO from "../../assets/original-icons/pco-logo-summary.svg";
import {
  TRANSACTION_STATUS,
  FINAL_STATUSES,
  RETRY_STATUSES,
  STATUS_TITLE_KEYS,
  DEFAULT_REDIRECT_URL,
} from "../../utils/summaryConstants";

const { APPROVED, DECLINED, ERROR, VOIDED, PENDING } = TRANSACTION_STATUS;
const PCOL_REFERENCE_CUMULATIVE_POINTS = 900;

const STATUS_CLASS_MAP = {
  [APPROVED]: "approved",
  [DECLINED]: "error",
  [ERROR]: "error",
  [PENDING]: "pending",
  [VOIDED]: "voided",
};

function getStatusClass(status) {
  return style[STATUS_CLASS_MAP[status]] || "";
}

function buildInfoRows(transaction, pmType, extra) {
  const rows = [
    {
      key: "id",
      label: i18next.t("summary.transactionInfo.id"),
      value: transaction.id,
    },
    {
      key: "reference",
      label: i18next.t("summary.transactionInfo.reference"),
      value: transaction.reference,
    },
  ];

  if (extra?.externalIdentifier && pmType !== "PSE") {
    const label =
      pmType === "DAVIPLATA"
        ? i18next.t("summary.transactionInfo.externalIdentifier")
        : i18next.t("summary.transactionInfo.intentionIdentifier");
    rows.push({ key: "extId", label, value: extra.externalIdentifier });
  }

  if (extra?.trazabilityCode) {
    rows.push({
      key: "trazabilityCode",
      label: i18next.t("summary.transactionInfo.traceabilityCode"),
      value: extra.trazabilityCode,
    });
  }

  if (extra?.ticketId) {
    rows.push({
      key: "ticketId",
      label: i18next.t("summary.transactionInfo.ticketId"),
      value: extra.ticketId,
    });
  }

  if (extra?.userLegalIdType && extra?.userLegalId) {
    rows.push(
      {
        key: "userLegalIdType",
        label: i18next.t("summary.transactionInfo.userLegalIdType"),
        value: extra.userLegalIdType,
      },
      {
        key: "userLegalId",
        label: i18next.t("summary.transactionInfo.userLegalId"),
        value: extra.userLegalId,
      },
    );
  }

  return rows;
}

function buildCustomerRows(transaction, pm, extra) {
  const rows = [];

  if (transaction.customerData?.fullName) {
    rows.push({
      key: "name",
      label: i18next.t("summary.transactionInfo.name"),
      value: transaction.customerData.fullName,
    });
  }
  if (transaction.customerData?.phoneNumber) {
    rows.push({
      key: "phone",
      label: i18next.t("summary.transactionInfo.number"),
      value: transaction.customerData.phoneNumber,
    });
  }
  if (transaction.customerEmail) {
    rows.push({
      key: "email",
      label: i18next.t("summary.transactionInfo.customerEmail"),
      value: transaction.customerEmail,
    });
  }

  if (!transaction.customerData && pm) {
    if (extra?.fullName)
      rows.push({
        key: "fullName",
        label: i18next.t("summary.transactionInfo.name"),
        value: extra.fullName,
      });
    if (extra?.cellphoneNumber)
      rows.push({
        key: "cellphoneNumber",
        label: i18next.t("summary.transactionInfo.number"),
        value: extra.cellphoneNumber,
      });
    if (extra?.email)
      rows.push({
        key: "email",
        label: i18next.t("summary.transactionInfo.customerEmail"),
        value: extra.email,
      });
  }

  const payerType = getPayerType(pm, extra);
  if (payerType) {
    rows.push({
      key: "r-payer",
      label: i18next.t("summary.transactionInfo.buyerType"),
      value: payerType,
    });
  }

  return rows;
}

function getPayerType(pm, extra) {
  if (pm?.userType === "PERSON" || extra?.userType === "person")
    return "Persona Natural";
  if (pm?.userType === "COMPANY" || extra?.userType === "company")
    return "Persona Jurídica";
  return null;
}

function renderRow({ key, label, value }) {
  if (value == null) return null;
  return (
    <div className={style.infoRow} key={key}>
      <span className={style.infoLabel}>{label}</span>
      <span className={style.infoValue} title={String(value || "")}>
        {value}
      </span>
    </div>
  );
}

const TAX_LABELS = { VAT: "IVA", CONSUMPTION: "Impuesto al consumo" };

class StandardSummary extends Component {
  constructor(props) {
    super(props);
    this.state = { countdownFinished: false };
    this.handleCountdownFinished = this.handleCountdownFinished.bind(this);
  }

  handleCountdownFinished() {
    this.setState({ countdownFinished: true });
  }

  componentDidMount() {
    const { store } = this.props;
    const { $state } = store;
    const { transaction } = $state;

    if (transaction?.canRetry == null) {
      store.$operations
        .getTransactionRetry(transaction.id)
        .then((res) => {
          store.$actions.setTransaction({
            ...transaction,
            canRetry: res?.data?.canRetry,
            signatureIntegrityRetry: res?.data?.signatureIntegrity,
          });
        })
        .catch((error) => console.error(error));
    }

    if ($state.PCOData?.isPCO && transaction.paymentMethodType !== "PCO") {
      store.$operations
        .getTransactionByNumber(
          transaction.paymentMethod?.extra?.parentTransactionId,
        )
        .then((res) => {
          store.$actions.setTransaction({
            ...store.$state.transaction,
            parentTransaction: res.data,
          });
        })
        .catch((error) => console.error(error));
    }
  }

  finish = (event) => {
    event.preventDefault();
    const { store } = this.props;
    store.$actions.redirectWithTransaction(
      store.$state.transaction.redirectUrl,
      store.$state.transaction.id,
    );
  };

  retryRedirect = () => {
    const { transaction } = this.props.store.$state;
    const checkoutUrl = String(process.env.CHECKOUT_URL);
    let redirectUrl = `${checkoutUrl}/retry_transaction/${transaction.id}`;
    if (!transaction?.paymentLinkId && transaction?.signatureIntegrity) {
      redirectUrl += `?signature=${transaction.signatureIntegrity}`;
    }
    globalThis.location.href = redirectUrl;
  };

  print = () => {
    const { store } = this.props;
    const { transaction, merchant } = store.$state;
    const merchantName = merchant
      ? merchant.name
      : transaction?.merchant?.name || "";
    console.log("[Receipt] Generating PDF for transaction:", transaction?.id);
    downloadReceiptPdf(transaction, merchantName);
  };
  canPrint = () => true;

  isRetryEligible = () => {
    const { transaction } = this.props.store.$state;
    return (
      RETRY_STATUSES.includes(transaction?.status) &&
      validateRetry(transaction, this.state.countdownFinished)
    );
  };

  getAccumulatedPoints = () => {
    const { transaction } = this.props.store.$state;
    const pt = transaction.parentTransaction;
    if (!pt?.paymentMethod?.extra?.remainingAmountInCents) return 0;
    return Number.parseInt(
      pt.paymentMethod.extra.remainingAmountInCents /
        100 /
        PCOL_REFERENCE_CUMULATIVE_POINTS,
    );
  };

  getPaymentMethodDetail(paymentMethodType, paymentMethod) {
    const extra = paymentMethod?.extra;
    const mask = (v) => (v ? `****${v.slice(-4)}` : "");
    switch (paymentMethodType) {
      case "DAVIPLATA":
        return paymentMethod?.userLegalId
          ? mask(paymentMethod.userLegalId)
          : paymentMethodType;
      case "NEQUI":
        return paymentMethod?.phoneNumber
          ? mask(paymentMethod.phoneNumber)
          : paymentMethodType;
      case "CARD":
        if (extra?.brand && extra?.lastFour)
          return `${extra.brand} ****${extra.lastFour}`;
        return extra?.brand || paymentMethodType;
      case "PSE": {
        const email = extra?.email || "";
        if (email) {
          const [local, domain] = email.split("@");
          const masked = local.length > 3 ? local.slice(0, -3) + "***" : "***";
          return `${masked}@${domain}`;
        }
        return extra?.financialInstitutionName || paymentMethodType;
      }
      default:
        return (
          i18next.t(`summary.paymentMethodType.${paymentMethodType}`) ||
          paymentMethodType
        );
    }
  }

  getPaymentMethodIcon(paymentMethodType, brand) {
    const brandIcons = {
      VISA: logoVisa,
      MASTERCARD: logoMastercard,
      AMEX: logoAmex,
      DINERS: logoDiners,
    };
    const methodIcons = {
      PSE: logoPSE,
      BANCOLOMBIA_TRANSFER: logoBancolombiaTransfer,
      BANCOLOMBIA_BNPL: logoBnpl,
      DAVIPLATA: logoDaviplata,
      SU_PLUS: logoSuPlusPay,
      PCO: logoPCO,
    };
    if (paymentMethodType === "CARD" && brand)
      return brandIcons[brand.toUpperCase()] || null;
    return methodIcons[paymentMethodType] || null;
  }

  renderIconByStatus = (status) => {
    if (status === VOIDED) return null;
    const map = {
      [APPROVED]: { src: statusApprovedIcon, alt: "approved" },
      [DECLINED]: { src: statusErrorIcon, alt: "declined" },
      [ERROR]: { src: statusErrorIcon, alt: "error" },
      [PENDING]: { src: statusPendingIcon, alt: "pending" },
    };
    const icon = map[status];
    return icon ? (
      <img src={icon.src} alt={icon.alt} width="40" height="40" />
    ) : null;
  };

  renderStatusHeader = (transaction) => {
    const { status, amountInCents, currency, createdAt } = transaction;
    const date = createdAt
      ? new Date(createdAt).toLocaleDateString("es-CO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) +
        " – " +
        new Date(createdAt).toLocaleTimeString("es-CO", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";
    const title = STATUS_TITLE_KEYS[status]
      ? i18next.t(STATUS_TITLE_KEYS[status])
      : status;
    const amount =
      typeof amountInCents === "number"
        ? `$${(amountInCents / 100).toLocaleString("es-CO", { minimumFractionDigits: 0 })} ${currency || ""}`
        : "";

    return (
      <div className={style.statusHeader}>
        <div className={style.statusIcon}>
          {this.renderIconByStatus(status)}
        </div>
        <span className={style.statusTitle}>{title}</span>
        <div className={`${style.amount} ${getStatusClass(status)}`}>
          {amount}
        </div>
        <span className={style.dateText}>{date}</span>
      </div>
    );
  };

  renderMessageBox = (transaction) => {
    const { status, statusMessage, customerEmail } = transaction;
    const email = transaction?.paymentMethod?.extra?.email || customerEmail;

    if (status === VOIDED || status === PENDING) return null;

    return (
      <div className={`${style.messageBox} ${getStatusClass(status)}`}>
        {status === APPROVED && (
          <div>
            <span className={style.messageBoxText}>
              {i18next.t("summary.messageBox.approved.line1")}
            </span>
            <span className={style.messageBoxText}>
              {i18next.t("summary.messageBox.approved.line2")}
            </span>
          </div>
        )}
        {[DECLINED, ERROR].includes(status) && (
          <div>
            {typeof statusMessage === "string" && statusMessage.length > 0 && (
              <span className={style.messageBoxText}>{statusMessage}</span>
            )}
            <span className={style.messageBoxText}>
              <strong>{i18next.t("summary.messageBox.error.noDebit")}</strong>
            </span>
          </div>
        )}
        {email && (
          <span className={style.messageBoxEmail}>
            {i18next.t("summary.messageBox.receiptSent")}{" "}
            <strong>{email}</strong>
          </span>
        )}
      </div>
    );
  };

  renderTransactionInfo = (transaction, isTransactionPCO) => {
    const titleTable = isTransactionPCO
      ? i18next.t("summary.transactionInfo.titleTable.PCOL")
      : i18next.t("summary.transactionInfo.titleTable.title");

    const pmType =
      transaction.paymentMethodType || transaction.paymentMethod?.type;
    const pm = transaction.paymentMethod;
    const extra = pm?.extra;

    const infoRows = buildInfoRows(transaction, pmType, extra);
    const customerRows = buildCustomerRows(transaction, pm, extra);
    const pmLabel = this.getPaymentMethodDetail(pmType, pm);
    const pmIcon = this.getPaymentMethodIcon(pmType, extra?.brand);

    return (
      <div className={style.infoSection}>
        <div className={style.infoSectionContent}>
          <span className={style.infoSectionTitle}>{titleTable}</span>
          {infoRows.map(renderRow)}
          {pm && (
            <div className={style.infoRow}>
              <span className={style.infoLabel}>
                {i18next.t("summary.transactionInfo.paymentMethod")}
              </span>
              <div className={style.infoValueWithIcon}>
                {pmIcon && (
                  <img
                    src={pmIcon}
                    alt={pmType || ""}
                    className={style.paymentBrandIcon}
                  />
                )}
                {pmLabel}
              </div>
            </div>
          )}
        </div>

        {customerRows.length > 0 && (
          <div className={style.infoSectionContent}>
            <hr className={style.separator} />
            <span className={style.infoSectionTitle}>
              {i18next.t("summary.payerInformation.title")}
            </span>
            {customerRows.map(renderRow)}
          </div>
        )}

        {transaction.taxes && transaction.taxes.length > 0 && (
          <div className={style.infoSectionContent}>
            <hr className={style.separator} />
            <span className={style.infoSectionTitle}>
              {i18next.t("summary.taxInformation.title")}
            </span>
            {transaction.taxes.map((tax) => (
              <div key={tax.type}>
                {renderRow({
                  key: `tax-type-${tax.type}`,
                  label: i18next.t("summary.transactionInfo.type"),
                  value: TAX_LABELS[tax.type] || tax.type,
                })}
                {renderRow({
                  key: `tax-amount-${tax.type}`,
                  label: i18next.t("summary.transactionInfo.amount"),
                  value: `$${(tax.amountInCents / 100).toLocaleString("es-CO")}`,
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderRetryView = (transaction, store) => {
    return (
      <div className={style.retry}>
        <div className={style.retryErrorBox}>
          <span className={style.retryErrorTitle}>
            {i18next.t("summary.retryBox.title")}
          </span>
          <div className={style.retryErrorBody}>
            {typeof transaction.statusMessage === "string" &&
              transaction.statusMessage.length > 0 && (
                <span>
                  {i18next.t("summary.retryBox.entityResponse")}{" "}
                  <strong>{transaction.statusMessage}</strong>
                </span>
              )}
            <span className={style.retryNoDebit}>
              {i18next.t("summary.retryBox.noDebit")}
            </span>
          </div>
        </div>
        <div className={style.moneyIcon}>
          <img
            src={retryIcon}
            alt="retry-icon"
            className={style.moneyIconImg}
          />
        </div>
        <div className={style.retryMessageSection}>
          <span
            className={style.retryMessage}
            dangerouslySetInnerHTML={{
              __html: i18next.t("summary.retryBox.retryMessage.line1"),
            }}
          />
          <span
            dangerouslySetInnerHTML={{
              __html: i18next.t("summary.retryBox.retryMessage.line2"),
            }}
          />
        </div>
        {transaction?.expirationTimeRetry && (
          <RetryTime
            store={store}
            onCountdownFinished={this.handleCountdownFinished}
          />
        )}
        {this.renderTransactionInfo(transaction, false)}
        <div className={style.actionButtons}>
          {this.canPrint() && (
            <button className={style.btnTertiary} onClick={this.print}>
              {i18next.t("summary.retryBox.downloadDetail")}
            </button>
          )}
          <button className={style.btnPrimary} onClick={this.retryRedirect}>
            {i18next.t("summary.actions.retryPayment")}
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { store } = this.props;
    const { $state } = store;
    const { transaction } = $state;

    if (!transaction?.status) {
      return (
        <div className={style.spinner}>
          <LoaderSpinner showText={false} />
        </div>
      );
    }

    const { status, redirectUrl, parentTransaction } = transaction;

    if (this.isRetryEligible()) {
      hotjarDispatch({ type: "event", action: "checkout_retry" });
      return this.renderRetryView(transaction, store);
    }

    const shouldRenderFinishButton =
      typeof redirectUrl === "string" &&
      !redirectUrl.includes(DEFAULT_REDIRECT_URL);
    const accumulatedPointsAmount = this.getAccumulatedPoints();

    return (
      <div className={style.standardSummary}>
        {FINAL_STATUSES.includes(status) && (
          <div className={style.card}>
            <div
              className={`${style.cardTopBar} ${style[status.toLowerCase()]}`}
            />
            {this.renderStatusHeader(transaction)}
            {status !== VOIDED &&
              status !== PENDING &&
              this.renderMessageBox(transaction)}
            {$state.PCOData?.isPCO &&
              status === APPROVED &&
              parentTransaction?.paymentMethod?.extra && (
                <PCOLSummaryBox
                  redeemedPoints={
                    parentTransaction.paymentMethod.extra.pointsRedeemed || 0
                  }
                  moneyAmount={
                    parentTransaction.paymentMethod.extra
                      .remainingAmountInCents || 0
                  }
                  accumulatedPoints={accumulatedPointsAmount}
                />
              )}
            {parentTransaction &&
              this.renderTransactionInfo(parentTransaction, true)}
            {this.renderTransactionInfo(transaction, false)}
          </div>
        )}
        {status === PENDING && (
          <div className={style.card}>
            <div className={`${style.cardTopBar} ${style.pending}`} />
            {this.renderStatusHeader(transaction)}
            <div className={style.infoSection}>
              <strong>{i18next.t("summary.pending.title")}</strong>
              <p>{i18next.t("summary.pending.message")}</p>
            </div>
          </div>
        )}
        <div className={style.actionButtons}>
          {shouldRenderFinishButton && (
            <button className={style.btnPrimary} onClick={this.finish}>
              {i18next.t("summary.actions.returnToMerchant")}
            </button>
          )}
          {this.canPrint() && (
            <button className={style.btnTertiary} onClick={this.print}>
              {i18next.t("summary.actions.downloadReceipt")}
            </button>
          )}
        </div>
      </div>
    );
  }
}

StandardSummary.propTypes = {
  store: PropTypes.object.isRequired,
};

export default StandardSummary;
